import { normalizeSku } from '../../utils/normalizeSku';

interface OrderAllowlistEntriesParams<T> {
  readonly entries: readonly T[];
  readonly getSku: (entry: T) => string;
  readonly pinnedSkus?: readonly string[];
}

export function orderAllowlistEntries<T>({
  entries,
  getSku,
  pinnedSkus = [],
}: OrderAllowlistEntriesParams<T>): T[] {
  const pinnedNorms = pinnedSkus.map((sku) => normalizeSku(sku));
  const pinnedNormSet = new Set(pinnedNorms);
  const byNormSku = new Map<string, T>();
  for (const entry of entries) {
    byNormSku.set(normalizeSku(getSku(entry)), entry);
  }

  const pinned: T[] = [];
  const seenPinned = new Set<string>();
  for (const sku of pinnedNorms) {
    if (seenPinned.has(sku)) continue;
    seenPinned.add(sku);
    const entry = byNormSku.get(sku);
    if (entry) {
      pinned.push(entry);
    }
  }

  const rest = entries
    .filter((entry) => !pinnedNormSet.has(normalizeSku(getSku(entry))))
    .sort((a, b) => getSku(a).localeCompare(getSku(b), undefined, { sensitivity: 'base' }));

  return [...pinned, ...rest];
}

export function pinAllowlistSku(pinnedSkus: readonly string[], sku: string): string[] {
  const pinnedNorm = normalizeSku(sku);
  return [sku, ...pinnedSkus.filter((existing) => normalizeSku(existing) !== pinnedNorm)];
}

export function unpinAllowlistSku(pinnedSkus: readonly string[], sku: string): string[] {
  const pinnedNorm = normalizeSku(sku);
  return pinnedSkus.filter((existing) => normalizeSku(existing) !== pinnedNorm);
}

export function excludeAllowlistedSkus(
  candidateSkus: readonly string[],
  allowlistedSkus: readonly string[]
): string[] {
  const allowlisted = new Set(allowlistedSkus.map((sku) => normalizeSku(sku)));
  return candidateSkus.filter((sku) => !allowlisted.has(normalizeSku(sku)));
}
